import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetIdentity, useLogout } from 'react-admin';

import { usePendingCount } from './data/pendientes';

import { BoMark, Ic, Wordmark } from './ui/icons';
import { ini } from './ui/fmt';
import { useOverlay } from './ui/overlays';
import { Pill, Tag } from './ui/prims';

// ─────────────────────────────────────────────────────────────────────────────
// ARMAZÓN. Reemplaza al <Layout> de react-admin (barra + menú lateral + tab bar
// móvil) por el chrome del prototipo: luz sobre seda, grano, cabecera de tres
// zonas con la marca a la izquierda, la navegación centrada en el eje de la
// página y las acciones a la derecha.
//
// Lo que NO cambia: las rutas. Cada sección apunta a la misma URL de siempre
// (`/transactions`, `/users`, `/currencies`, `/metrics`, `/settings`), así que
// los enlaces guardados, el botón de atrás del navegador y la redirección tras
// aprobar una transacción siguen funcionando igual.
// ─────────────────────────────────────────────────────────────────────────────

const ICO: Record<string, React.ReactNode> = {
  hoy: (
    <>
      <path d="M3.5 10.5 12 3.5l8.5 7" />
      <path d="M6 9.6V20h12V9.6" />
    </>
  ),
  mesa: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2.4" />
      <path d="M3 10.5h18" />
      <path d="M7.5 14.6h4" />
    </>
  ),
  inv: (
    <>
      <circle cx="9.2" cy="8" r="3.3" />
      <path d="M3.4 19c0-3.1 2.6-5.2 5.8-5.2S15 15.9 15 19" />
      <path d="M16.2 6.3a3 3 0 0 1 0 5.5M18.6 19c0-2-.6-3.5-1.7-4.6" />
    </>
  ),
};

export type SecId = 'hoy' | 'mesa' | 'inv' | 'act' | 'uso' | 'aj';

export const SECS: Array<{ id: SecId; l: string; path: string }> = [
  { id: 'hoy', l: 'Hoy', path: '/' },
  { id: 'mesa', l: 'Mesa', path: '/transactions' },
  { id: 'inv', l: 'Inversores', path: '/users' },
  { id: 'act', l: 'Activos', path: '/currencies' },
  { id: 'uso', l: 'Analíticas', path: '/metrics' },
  { id: 'aj', l: 'Ajustes', path: '/settings' },
];

/** Qué sección corresponde a la ruta actual (la ficha cuenta como Inversores). */
export function secDeRuta(pathname: string): SecId {
  if (pathname.startsWith('/transactions')) return 'mesa';
  if (pathname.startsWith('/users')) return 'inv';
  if (pathname.startsWith('/currencies')) return 'act';
  if (pathname.startsWith('/metrics') || pathname.startsWith('/holdings')) return 'uso';
  if (pathname.startsWith('/settings')) return 'aj';
  return 'hoy';
}

// ── Tema ─────────────────────────────────────────────────────────────────────
const TEMA_KEY = 'yb-bo-tema';
const DENS_KEY = 'yb-bo-densidad';

function leerTema(): 'dark' | 'light' {
  try {
    return localStorage.getItem(TEMA_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function useTema(): ['dark' | 'light', (t: 'dark' | 'light') => void] {
  // Lo que depende del navegador se lee en un EFECTO, nunca en el render
  // (aprendizaje de la bitácora: la app hidrataba otro texto). Acá no hay SSR,
  // pero la regla se respeta igual para no dejar el patrón torcido.
  const [t, setT] = useState<'dark' | 'light'>('dark');

  useEffect(() => {
    setT(leerTema());
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('light', t === 'light');
    try {
      localStorage.setItem(TEMA_KEY, t);
    } catch {
      /* modo privado: el tema no persiste, la consola funciona igual */
    }
  }, [t]);

  return [t, setT];
}

// ── Densidad ─────────────────────────────────────────────────────────────────
// fase67 — La consola nació con la piel de la web, que es una superficie de
// lectura pausada: 26 px entre bloques y filas de 64. Para mirar el patrimonio
// está bien; para atender doscientos movimientos significa doce renglones por
// pantalla. La vista compacta recorta espacio y prosa —no controles ni datos— y
// se guarda como el tema. La cómoda sigue siendo la de siempre, que es la que
// el arnés de piel mide contra el prototipo.
export type Densidad = 'comoda' | 'compacta';

function leerDensidad(): Densidad {
  try {
    return localStorage.getItem(DENS_KEY) === 'compacta' ? 'compacta' : 'comoda';
  } catch {
    return 'comoda';
  }
}

export function useDensidad(): [Densidad, (d: Densidad) => void] {
  const [d, setD] = useState<Densidad>('comoda');

  useEffect(() => {
    setD(leerDensidad());
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(DENS_KEY, d);
    } catch {
      /* modo privado: no persiste, la consola funciona igual */
    }
  }, [d]);

  return [d, setD];
}

// ── Pegado de las barras de pestañas ─────────────────────────────────────────
// Cada barra `.tabs.pega` vigila su propio centinela `.sent`: al alcanzar el
// tope se pega bajo la cabecera, extiende su fondo a todo el ancho y su línea
// se vuelve continua. El observador se re-arma cuando cambia la ruta, porque
// las barras nacen y mueren con cada pantalla.
export function useVigilarPegado(dep: unknown) {
  useEffect(() => {
    const alto = 66; // var(--hh); --pbar es 0 en producción
    const obs: IntersectionObserver[] = [];
    const barras = Array.from(document.querySelectorAll<HTMLElement>('.yb-c .tabs.pega'));
    barras.forEach((bar) => {
      const sent = bar.previousElementSibling;
      if (!sent || !sent.classList.contains('sent')) return;
      const o = new IntersectionObserver(
        ([e]) => {
          bar.classList.toggle(
            'stuck',
            !e.isIntersecting && e.boundingClientRect.top < alto
          );
        },
        { rootMargin: `-${alto}px 0px 0px 0px`, threshold: 0 }
      );
      o.observe(sent);
      obs.push(o);
    });
    return () => obs.forEach((o) => o.disconnect());
  }, [dep]);
}

// ── Globo de los íconos de ayuda ─────────────────────────────────────────────
// El `.tip` vive dentro del `.inf` para que el contenido esté al lado de lo que
// explica; el globo VISIBLE se dibuja en un nodo suelto y posicionado a mano,
// así no hereda el recorte ni la opacidad de su rótulo. Un solo oyente
// delegado, como en el prototipo.
function useGlobo() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const g = ref.current;
    if (!g) return;
    let dentro: Element | null = null;
    const ocultar = () => {
      if (!dentro) return;
      dentro = null;
      g.classList.remove('on');
    };
    const over = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      const inf = t?.closest?.('.inf');
      if (!inf) {
        ocultar();
        return;
      }
      if (inf === dentro) return;
      const tip = inf.querySelector('.tip');
      if (!tip) return;
      dentro = inf;
      g.textContent = tip.textContent || '';
      g.classList.add('on');
      const r = inf.getBoundingClientRect();
      const b = g.getBoundingClientRect();
      let x = r.left + r.width / 2 - b.width / 2;
      let y = r.top - b.height - 10;
      x = Math.max(12, Math.min(x, window.innerWidth - b.width - 12));
      if (y < 12) y = r.bottom + 10;
      g.style.transform = `translate(${Math.round(x)}px,${Math.round(y)}px)`;
    };
    document.addEventListener('mouseover', over);
    window.addEventListener('scroll', ocultar, { passive: true });
    window.addEventListener('blur', ocultar);
    return () => {
      document.removeEventListener('mouseover', over);
      window.removeEventListener('scroll', ocultar);
      window.removeEventListener('blur', ocultar);
    };
  }, []);

  return <div id="glob" ref={ref} />;
}

// ── Menú del administrador ───────────────────────────────────────────────────
function MenuAdmin({
  tema,
  setTema,
  densidad,
  setDensidad,
}: {
  tema: 'dark' | 'light';
  setTema: (t: 'dark' | 'light') => void;
  densidad: Densidad;
  setDensidad: (d: Densidad) => void;
}) {
  const [open, setOpen] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const history = useHistory();
  const ov = useOverlay();

  // React 19 delega en `document`: `click` + `stopPropagation` no alcanza.
  // Se cierra con `mousedown` comprobando contención (aprendizaje de la
  // bitácora; acá seguimos en React 17, pero el patrón es el correcto y no
  // depende de la versión).
  useEffect(() => {
    if (!open) return;
    const fuera = (e: MouseEvent) => {
      if (!caja.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const nombre = String(identity?.fullName || 'Administrador');
  const dia = tema === 'light';

  const salir = () =>
    ov.modal({
      title: 'Cerrar la sesión',
      body: 'Vas a salir de la consola. En el próximo ingreso te va a pedir la contraseña y el código de tu segundo factor.',
      foot: (
        <>
          <Pill kind="q" onClick={ov.cerrar}>
            Cancelar
          </Pill>
          <Pill
            kind="dgr"
            onClick={() => {
              ov.cerrar();
              logout();
            }}
          >
            Cerrar la sesión
          </Pill>
        </>
      ),
    });

  return (
    <div style={{ position: 'relative' }} ref={caja}>
      <button
        className={`userbtn${open ? ' on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="av">{ini(nombre)}</span>
        <span className="un">{nombre.split(' ')[0]}</span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: 'var(--faint)',
            transition: 'transform .24s var(--ease)',
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <div className={`pop${open ? ' on' : ''}`}>
        <div className="pop-h">
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                fontSize: '.68rem',
                fontWeight: 500,
                flex: 'none',
                color: 'var(--cap)',
                background: 'color-mix(in srgb,var(--cap) 14%,transparent)',
                border: '1px solid color-mix(in srgb,var(--cap) 28%,transparent)',
              }}
            >
              {ini(nombre)}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '.86rem' }}>{nombre}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 7, marginTop: 13, flexWrap: 'wrap' }}>
            <Tag kind="cp" dot={false}>
              Administrador
            </Tag>
            <Tag kind="ok">Segundo factor</Tag>
          </div>
        </div>
        <div style={{ padding: '7px 0' }}>
          <button
            className="pop-i"
            type="button"
            onClick={() => {
              setOpen(false);
              setTema(dia ? 'dark' : 'light');
            }}
          >
            <Ic n={dia ? 'sol' : 'luna'} size={15} />
            {dia ? 'Pasar a noche' : 'Pasar a día'}
            <span className="aux">{dia ? 'día' : 'noche'}</span>
          </button>
          <button
            className="pop-i"
            type="button"
            onClick={() => {
              setOpen(false);
              setDensidad(densidad === 'compacta' ? 'comoda' : 'compacta');
            }}
            title="La vista compacta achica filas y márgenes y esconde los textos de ayuda de cada pantalla. No cambia ni un dato ni un botón."
          >
            <Ic n="barras" size={15} />
            {densidad === 'compacta' ? 'Pasar a vista cómoda' : 'Pasar a vista compacta'}
            <span className="aux">{densidad === 'compacta' ? 'compacta' : 'cómoda'}</span>
          </button>
          <div className="pop-sep" />
          <button
            className="pop-i"
            type="button"
            onClick={() => {
              setOpen(false);
              history.push('/settings?t=admin');
            }}
          >
            <Ic n="persona" size={15} />
            Administración
          </button>
          <button
            className="pop-i"
            type="button"
            onClick={() => {
              setOpen(false);
              history.push('/settings?t=audit');
            }}
          >
            <Ic n="reloj" size={15} />
            Registro de auditoría
          </button>
          <div className="pop-sep" />
          <button
            className="pop-i dgr"
            type="button"
            onClick={() => {
              setOpen(false);
              salir();
            }}
          >
            <Ic n="salir" size={15} />
            Cerrar la sesión
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Armazón ──────────────────────────────────────────────────────────────────
export function ConsoleShell({ children }: { children: React.ReactNode }) {
  const [tema, setTema] = useTema();
  const [densidad, setDensidad] = useDensidad();
  const location = useLocation();
  const history = useHistory();
  const pendientes = usePendingCount();
  const globo = useGlobo();
  const ov = useOverlay();

  const sec = secDeRuta(location.pathname);

  // El fondo del documento acompaña a la consola mientras esté montada; al
  // desmontarse (pantalla de ingreso) vuelve el claro de MUI.
  useEffect(() => {
    document.documentElement.classList.add('yb-dark');
    return () => {
      document.documentElement.classList.remove('yb-dark');
    };
  }, []);

  const bdg = useCallback(
    (id: SecId) => (id === 'mesa' && pendientes ? pendientes : 0),
    [pendientes]
  );

  const nav = useMemo(
    () =>
      SECS.map((s) => {
        const b = bdg(s.id);
        return (
          <button
            key={s.id}
            type="button"
            className={sec === s.id ? 'on' : undefined}
            onClick={() => history.push(s.path)}
          >
            {s.l}
            {b ? <span className="bdg num">{b}</span> : null}
          </button>
        );
      }),
    [sec, bdg, history]
  );

  return (
    <div className={`yb-c${densidad === 'compacta' ? ' dense' : ''}`}>
      <div className="amb">
        <span className="pool a" />
        <span className="pool b" />
      </div>
      <div className="grain" />

      <header id="hdr">
        <div className="hin">
          <a
            className="brand"
            href="/"
            aria-label="Yieldbox · BO"
            onClick={(e) => {
              e.preventDefault();
              history.push('/');
            }}
          >
            <Wordmark />
            <span className="bsep" />
            <BoMark />
          </a>
          <nav className="nav">{nav}</nav>
          <div className="hact">
            <button
              className="iconbtn"
              title="Avisos"
              type="button"
              onClick={() =>
                ov.toast('Los avisos del sistema viven en la portada, arriba de todo.')
              }
            >
              <Ic n="campana" />
            </button>
            <MenuAdmin tema={tema} setTema={setTema} densidad={densidad} setDensidad={setDensidad} />
          </div>
        </div>
      </header>

      <main id="main">
        <div className="wrap">{children}</div>
      </main>

      {globo}
    </div>
  );
}

export { ICO };
